import classnames from 'classnames';
import { type ActionDispatch, useEffect, useState } from 'react';
import { type NavigateFunction, useNavigate } from 'react-router-dom';
import { CollaborationProvider } from '../collaboration/CollaborationProvider';
import { getOrgTenantCount } from '../sources/organization';
import { allowed } from '../sources/tenant';
import type { UserResponseAPI, UserTenantResponseAPI } from '../types/user';
import { hasSsoSession } from '../utils/auth';
import AdminSidebar from './AdminSidebar';
import IdleTimeout from './authentication/IdleTimeout';
import Footer from './Footer';
import FeatureFlagGuard from './featureFlags/FeatureFlagGuard';
import { FeatureFlagProvider } from './featureFlags/FeatureFlagProvider';
import Header from './header/Header';
import PlatformMasthead from './masthead/Masthead';
import TenantSwitcher from './masthead/TenantSwitcher';
import NotificationList from './notifications/NotificationList';
import {
    type NotificationAction,
    useNotificationDispatch,
} from './notifications/NotificationProvider';
import Tenant from './Tenant';
import { TenantProvider } from './TenantProvider';
import { TabsProvider } from './tabs/TabsProvider';
import { useUser } from './UserProvider';

type PendoOptions = {
    visitor: {
        id: string;
        email: string;
        full_name: string;
    };
    account: {
        id: string;
        name: string;
        expires: string;
    };
    parentAccount: {
        id: string;
        name: string | undefined;
    };
};

declare global {
    interface Window {
        pendo: {
            visitorId: string | undefined;
            identify: (options: PendoOptions) => unknown;
            initialize: (options: PendoOptions) => unknown;
        };
    }
}

const initializeTenant = async (
    tenants: UserTenantResponseAPI[] | undefined,
    navigate: NavigateFunction,
    dispatchNotification: ActionDispatch<[action: NotificationAction]>,
) => {
    if (!tenants || tenants.length === 0) {
        dispatchNotification({
            type: 'notifyError',
            payload: { error: 'You do not have access to any tenants.' },
        });
        return undefined;
    }

    const tenantIdFromUrl = window.location.pathname.split('/').filter((p) => !!p)[0];
    let tenant = tenantIdFromUrl ? tenants.find((t) => t.id === tenantIdFromUrl) : tenants[0];

    let errorMessage = '';

    // Tenant Id was specified in the url but we couldn't find a matching tenant for the logged in user
    if (!tenant && tenantIdFromUrl) {
        errorMessage = `You are not a member of the tenant with id ${tenantIdFromUrl}`;
    }

    //Check to see if the user is a member of any active tenants
    const firstActiveTenant = tenants.find((t) => !t.isExpired);
    if (!firstActiveTenant) {
        errorMessage = `Your user account doesn't have access to any active tenants. They have been either expired or deactivated.`;
        tenant = undefined;
    }

    if (tenant) {
        // For expired tenants stick an error notification on the screen, this will also pop open the tenant selector
        if (tenant.isExpired) {
            errorMessage = `Your trial expired on ${tenant.expiresAt} for tenant ${tenant.developerName} and has been deactivated.`;

            tenant = undefined;
        }

        if (tenant) {
            // Check that we have definitive access to the tenant i.e. not blocked by ip restrictions.
            // The organization tenant count is requested in parallel as it doesn't depend on the access check.
            const [accessResult, countResult] = await Promise.allSettled([
                allowed(tenant.id),
                tenant.organization ? getOrgTenantCount(tenant.id) : undefined,
            ]);

            if (accessResult.status === 'rejected') {
                errorMessage = `Your user account doesn't have access to the Tenant ${tenant.developerName}`;
                tenant = undefined;
            } else {
                // If there wasn't a tenant id in the url but the user is a member of at least one non-expired tenant then
                // navigate to the flows page
                if (!tenantIdFromUrl && firstActiveTenant) {
                    navigate(`/${firstActiveTenant.id}/flows`);
                }

                if (countResult.status === 'rejected') {
                    errorMessage = `Could not find the number of tenants in this Organization`;
                } else if (tenant.organization && countResult.value !== undefined) {
                    tenant.organization.tenantCount = countResult.value;
                }
            }
        }
    }

    if (!tenant) {
        window.history.replaceState({}, document.title, '');
    }

    if (errorMessage !== '') {
        dispatchNotification({
            type: 'notifyError',
            payload: { error: errorMessage },
        });
    }
    return tenant;
};

const initializePendo = (user: UserResponseAPI, tenant: UserTenantResponseAPI) => {
    try {
        const pendo = window.pendo;

        if (!(pendo && user && tenant)) {
            return;
        }

        const options = {
            visitor: {
                id: user.id,
                email: user.email,
                full_name: `${user.firstName} ${user.lastName}`,
            },
            account: {
                id: tenant.id,
                name: tenant.developerName,
                expires: new Date(tenant.expiresAt).toISOString(),
            },
            parentAccount: {
                id: tenant.organization ? tenant.organization.id : 'no-organization',
                name: tenant.organization?.name,
            },
        };

        if (pendo.visitorId) {
            pendo.identify(options);
        } else {
            pendo.initialize(options);
        }
    } catch (error) {
        console.error('Failed to initialize Pendo:\n', error);
    }
};

/**
 *
 * @description The base component that renders all parts of the application that needs
 * to display the header and footer.
 */
const Tooling = () => {
    const [tenantIsInitialized, setTenantIsInitialized] = useState(false);
    const [isTenantSelectorOpen, setIsTenantSelectorOpen] = useState(false);
    const [tenant, setTenant] = useState<UserTenantResponseAPI | null>(null);
    const [tenantModalContainer, setTenantModalContainer] = useState<HTMLElement | null>(null);
    const navigate = useNavigate();
    const dispatchNotification = useNotificationDispatch();
    const isSsoSession = hasSsoSession();
    const user = useUser();

    const onCloseTenantSelector = () => setIsTenantSelectorOpen(false);
    const openTenantSelector = () => setIsTenantSelectorOpen(true);

    useEffect(() => {
        initializeTenant(user.tenants, navigate, dispatchNotification)
            .then((tenant) => {
                setTenant(tenant ?? null);
                if (tenant) {
                    // Initialize Pendo with the user and tenant information
                    initializePendo(user, tenant);
                }
            })
            .finally(() => {
                setTenantIsInitialized(true);
            });
    }, [user, navigate, dispatchNotification]);

    const noTenantSelected = tenantIsInitialized && tenant === null;
    const showTenantSwitcher = isTenantSelectorOpen || noTenantSelected;

    // A tenant must be chosen before the selector can be dismissed
    const onMastheadClick = () => {
        if (isTenantSelectorOpen && tenant) {
            onCloseTenantSelector();
        }
    };

    return (
        <TabsProvider>
            <FeatureFlagProvider tenantId={tenant?.id ?? ''}>
                <div className="draw">
                    {isSsoSession ? (
                        <Header openTenantSelector={openTenantSelector} tenant={tenant} />
                    ) : (
                        // Capture phase so that "Switch tenant" in the masthead can still re-open the selector
                        <div
                            className={classnames('masthead-header', {
                                'masthead-header-above-backdrop': showTenantSwitcher,
                            })}
                            data-testid="platform-masthead"
                            onClickCapture={onMastheadClick}
                        >
                            <PlatformMasthead
                                tenant={tenant}
                                openTenantSelector={openTenantSelector}
                            />
                        </div>
                    )}
                    <div className="tenant-modal-container" ref={setTenantModalContainer} />
                    {tenant !== null && <AdminSidebar tenant={tenant} />}
                    {showTenantSwitcher ? (
                        <TenantSwitcher
                            onClose={onCloseTenantSelector}
                            currentTenant={tenant}
                            // Keeps the masthead usable so the user can sign out or switch account
                            container={tenantModalContainer}
                        />
                    ) : null}
                    {tenant && (
                        <FeatureFlagGuard>
                            <TenantProvider tenant={tenant}>
                                {tenant.isSso && <IdleTimeout />}
                                <CollaborationProvider tenantId={tenant.id}>
                                    <div className="tab-outer">
                                        <Tenant
                                            isTenantSelectorOpen={isTenantSelectorOpen}
                                            tenant={tenant}
                                        />
                                    </div>
                                </CollaborationProvider>
                            </TenantProvider>
                        </FeatureFlagGuard>
                    )}
                    <Footer />
                    <NotificationList />
                </div>
            </FeatureFlagProvider>
        </TabsProvider>
    );
};

export default Tooling;
